<template>
    <div class="flex flex-col gap-6 rounded-2xl border border-border bg-surface-raised p-6 shadow-sm md:p-10">
        <div v-focus="focusOnMount" tabindex="-1">
            <BasePageTitle
                :title="firstName ? t('profile.collaboration.acceptShared.confirmTitle', { name: firstName }) : t('profile.collaboration.acceptShared.confirmTitleAnonymous')">
                <template #subtitle>
                    {{
                        firstName ? t("profile.collaboration.acceptShared.confirmSubtitle", { name: firstName }) : t("profile.collaboration.acceptShared.confirmSubtitleAnonymous")
                    }}
                </template>
            </BasePageTitle>
        </div>
        <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
        <div class="flex flex-col gap-3">
            <p class="scaffold-details-small text-ink-muted">{{ t("profile.collaboration.acceptShared.possibleMatches") }}</p>
            <ul class="max-h-80 divide-y divide-border overflow-auto border-t border-border" :aria-label="t('profile.collaboration.acceptShared.possibleMatchesAriaLabel')">
                <li v-for="card in candidates" :key="card.connection_id" class="flex items-center gap-3 py-3 pr-3">
                    <CollaborationProfileAvatar size="sm" :name="card.name ?? ''" />
                    <span class="flex min-w-0 grow flex-col">
                        <span :id="`name-${card.connection_id}`" class="body-small-semibold truncate">{{ card.name }}</span>
                        <span :id="`detail-${card.connection_id}`" class="body-small truncate text-ink-muted">{{ subtitleFor(card) }}</span>
                    </span>
                    <BaseButton
                        :id="`select-${card.connection_id}`"
                        :aria-labelledby="`select-${card.connection_id} name-${card.connection_id} detail-${card.connection_id}`"
                        theme="primary"
                        size="sm"
                        class="min-w-18 justify-center"
                        :disabled="busy"
                        @click="emit('select', card.connection_id)">
                        {{ t("profile.collaboration.acceptShared.select") }}
                    </BaseButton>
                </li>
            </ul>
        </div>
        <div class="flex justify-center border-t border-border pt-6">
            <BaseButton theme="primary" :disabled="busy" @click="emit('createNew')">{{ t("profile.collaboration.acceptShared.addAsNew") }}</BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ConnectionMatchCandidate } from "~vue/api/generated/collab-profiles";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import { vFocus } from "~vue/directives/vFocus";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps<{
    candidates: ConnectionMatchCandidate[];
    firstName: string;
    error?: string | null;
    /** Disables the resolution buttons while an accept request is pending. */
    busy?: boolean;
    /** Move focus to the title when it mounts. Only for page flows where the control the user pressed was just swapped out; dialogs manage their own focus. */
    focusOnMount?: boolean;
}>();

const emit = defineEmits<{
    select: [connectionId: string];
    createNew: [];
}>();

const subtitleFor = (card: ConnectionMatchCandidate): string => card.job_title || t("profile.collaboration.acceptShared.addedManually");
</script>
